import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import LoadingIndicator from '@/components/LoadingIndicator';
import PERMISSIONS from '@/constants/auth.constants';
import TicketFeedbackRating from '@/features/TicketFeedbackRating';
import { TICKET_TYPE } from '@/pages/Ticket';
import { useUpdateStatus } from '@/pages/Ticket/Ticket.store';
import type { TicketStatusDialog } from '@/pages/Ticket/Ticket.types';

import type {
  TicketStatusDialogServiceProps,
  TicketStatusDialogServiceSubmission
} from './TicketStatusDialogService.types';

const TicketStatusDialogService = (props: TicketStatusDialogServiceProps) => {
  const { newStatus, open, setOpen } = props;

  const form = useForm<TicketStatusDialog>({
    defaultValues: {
      message: '',
      feedback_rating: undefined,
      feedback_reason: undefined
    }
  });
  const { control, handleSubmit, reset } = form;
  const formMessage = useWatch({ control, name: 'message' });
  const formFeedbackRating = useWatch({ control, name: 'feedback_rating' });

  const { isLoadingUpdateStatus, updateStatus } = useUpdateStatus(setOpen);
  const MIN_CHAR_LIMIT = 10;

  const handelCancel = () => {
    reset();
    setOpen(false);
  };

  const onSubmit: SubmitHandler<TicketStatusDialogServiceSubmission> = (
    data
  ) => {
    if (newStatus) {
      const requestBody = {
        message: data.message,
        ticket_status_id: newStatus.id,
        feedback_rating: data.feedback_rating,
        feedback_reason: data.feedback_reason
      };
      updateStatus(requestBody);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <LoadingIndicator isLoading={isLoadingUpdateStatus} />
      <DialogTitle>Close Ticket</DialogTitle>
      <DialogContent>
        <FormProvider {...form}>
          <form noValidate>
            <Stack spacing={2} sx={{ py: 2, width: '100%' }}>
              <Typography>
                Resolve and close the ticket as fully addressed, with no further
                action needed.
              </Typography>
              <CheckPermissions permissions={[PERMISSIONS.NOT_INTERNAL]}>
                <Controller
                  control={control}
                  name="feedback_rating"
                  render={({ field }) => (
                    <Controller
                      control={control}
                      name="feedback_reason"
                      render={(feedbackReason) => (
                        <TicketFeedbackRating
                          ticketType={TICKET_TYPE.SERVICE}
                          field={field}
                          feedbackReason={feedbackReason}
                          formFeedbackRating={formFeedbackRating}
                        />
                      )}
                    />
                  )}
                />
              </CheckPermissions>
              <div>
                <InputLabel htmlFor="message-input">Message *</InputLabel>
                <Controller
                  control={control}
                  name="message"
                  rules={{
                    minLength: MIN_CHAR_LIMIT,
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <FormControl fullWidth>
                      <TextField
                        id="message-input"
                        multiline
                        error={!!formState.errors.message}
                        {...field}
                      />
                    </FormControl>
                  )}
                />
                <small>{`minimum ${MIN_CHAR_LIMIT} characters`}</small>
              </div>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handelCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={(formMessage?.length ?? 0) < MIN_CHAR_LIMIT}
          onClick={handleSubmit(onSubmit)}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketStatusDialogService;
